<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <p class="mb-2 font-weight-bold">Title</p>
            <ckeditor :editor="editor" v-model="fields.header.title" :config="editorConfig" />
            <div class="mt-4">
              <DividerPrimary :divider="fields.divider" label="Divider" />
            </div>
            <div class="mt-4">
              <p class="mb-2 font-weight-bold">Description</p>
              <ckeditor :editor="editor" v-model="fields.header.description" :config="editorConfig" />
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Steps Grid</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="fields.grid.columns"
                  label="Columns Per View"
                  type="number"
                  min="1"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="fields.grid.gap" label="Gap (px)" type="number" min="0" hide-details />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.grid.stepNumber.backgroundColor"
                  name="stepNumberBackgroundColor"
                  label="Step Number Background"
                  default-color="#ffffff"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.grid.stepNumber.textColor"
                  name="stepNumberTextColor"
                  label="Step Number Text Color"
                  default-color="#0130E2"
                />
              </v-col>
            </v-row>
            <div class="d-flex justify-end mt-4 mb-2">
              <v-btn small text color="primary" @click="handleAddStep">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add Step</span>
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.steps" :key="`step-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center" style="gap: 10px">
                      <img
                        :src="item.stepImage.src"
                        alt=""
                        style="
                          width: 48px;
                          height: 48px;
                          object-fit: cover;
                          border: 1px solid #e0e0e0;
                          border-radius: 4px;
                        "
                      />
                      <div class="line-clamp-1" v-html="item.content.title.replace(/<[^>]*>/g, '').trim()"></div>
                    </div>
                    <v-btn icon @click.stop="handleRemoveStep(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <p class="mb-2 font-weight-bold">Step Number</p>
                  <v-text-field v-model="item.stepNumber.text" label="Text" hide-details class="mb-4" />
                  <p class="mb-2 font-weight-bold">Step Image</p>
                  <UploadImage
                    @callback="obj => handleUploadStepImage(obj, index)"
                    :image="{ imageSrc: item.stepImage.src }"
                    :height="`180`"
                    :typeUpload="'image'"
                    @callbackDelete="() => handleRemoveStepImage(index)"
                  />
                  <p class="mb-2 mt-4 font-weight-bold">Content Icon</p>
                  <UploadImage
                    @callback="obj => handleUploadContentIcon(obj, index)"
                    :image="{ imageSrc: item.content.iconSrc }"
                    :height="`80`"
                    :typeUpload="'image'"
                    @callbackDelete="() => handleRemoveContentIcon(index)"
                  />
                  <p class="mb-2 mt-4 font-weight-bold">Content Title</p>
                  <ckeditor :editor="editor" v-model="item.content.title" :config="editorConfig" />
                  <p class="mb-2 mt-4 font-weight-bold">Content Description</p>
                  <ckeditor :editor="editor" v-model="item.content.description" :config="editorConfig" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import DividerPrimary from './common/divider-primary.vue';
import ColorPicker from './common/color-picker.vue';
import { replaceImageUrl } from '../common/utils';

const defaultStepItem = () => ({
  stepNumber: {
    text: '01',
  },
  stepImage: {
    src: replaceImageUrl('https://cdn.skyfastimg.com/images/2026/06/04/image-6a211e953ae0a76b18783692.jpg'),
  },
  content: {
    iconSrc: replaceImageUrl('https://cdn.skyfastimg.com/images/2026/06/04/image-6a211eaa3280cbb93c683c93.png'),
    title: '<p><strong>Connect To TV Coaxial</strong></p>',
    description: '<p>Using the provided cable, connect the device to your television set.</p>',
  },
});

export default {
  name: 'fastvaluedeal-card-step-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
    DividerPrimary,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Fastvaluedeal Card Step',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        header: {
          title: '<h2 style="text-align: center"><strong>Enjoy Free HDTV In 3 Easy Steps</strong></h2>',
          description:
            '<p style="text-align: center">Setting up WaveMax is incredibly simple. Just follow these three easy steps to start enjoying free HDTV channels in your home.</p>',
        },
        divider: {
          show: true,
          maxWidth: 90,
          height: 6,
          backgroundColor: '#5B04DE',
          align: 'center',
        },
        grid: {
          columns: 3,
          gap: 20,
          stepNumber: {
            backgroundColor: '#ffffff',
            textColor: '#0130E2',
          },
        },
        steps: [defaultStepItem(), defaultStepItem(), defaultStepItem()],
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        header: { ...this.fields.header, ...(extract.header || {}) },
        divider: { ...this.fields.divider, ...(extract.divider || {}) },
        grid: {
          ...this.fields.grid,
          ...(extract.grid || {}),
          stepNumber: {
            ...this.fields.grid.stepNumber,
            ...(extract.grid?.stepNumber || {}),
          },
        },
        steps: Array.isArray(extract.steps)
          ? extract.steps.map(item => ({
              stepNumber: {
                text: item.stepNumber?.text || '01',
              },
              stepImage: {
                src: item.stepImage?.src || '',
              },
              content: {
                iconSrc: item.content?.iconSrc || '',
                title: item.content?.title || '',
                description: item.content?.description || '',
              },
            }))
          : this.fields.steps,
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const payload = {
        ...this.fields,
        steps: (this.fields.steps || []).map(item => ({
          stepNumber: {
            text: item.stepNumber?.text || '',
          },
          stepImage: {
            src: item.stepImage?.src || '',
          },
          content: {
            iconSrc: item.content?.iconSrc || '',
            title: item.content?.title || '',
            description: item.content?.description || '',
          },
        })),
      };
      const updateHtml = updateHtmlFromData.sectionFastvaluedealCardStep(this.sectionEditor.section.html, payload);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddStep() {
      this.fields.steps.push(defaultStepItem());
    },
    handleRemoveStep(index) {
      this.fields.steps.splice(index, 1);
    },
    handleUploadStepImage(obj, index) {
      if (!this.fields.steps[index]) return;
      this.$set(this.fields.steps[index].stepImage, 'src', obj.imageSrc || '');
    },
    handleRemoveStepImage(index) {
      if (!this.fields.steps[index]) return;
      this.$set(this.fields.steps[index].stepImage, 'src', '');
    },
    handleUploadContentIcon(obj, index) {
      if (!this.fields.steps[index]) return;
      this.$set(this.fields.steps[index].content, 'iconSrc', obj.imageSrc || '');
    },
    handleRemoveContentIcon(index) {
      if (!this.fields.steps[index]) return;
      this.$set(this.fields.steps[index].content, 'iconSrc', '');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
::v-deep .ck-editor__editable_inline {
  min-height: 120px !important;
}
</style>
